import React, { useCallback, useEffect, useMemo } from 'react';
import { Text, Paragraph, Content, ContentSwitcherWrapper, NoOlympiads, TextColumn } from './olympiadsStyled';
import SwitchTabs, { BluePillsTab, SwitchTabsType } from 'features/shared/SwitchTabs';
import GeneralStatistics from './components/GeneralStatistics';
import OlympiadDetails from './components/OlympiadDetails';
import LoaderComponent from 'features/shared/layouts/main/ui/components/loader';
import { useClickStreamContext } from '../shared/ClickStream';
import Spacer from '../shared/Spacer';
import useUserData from 'hooks/useUserData';
import { Container, Header, Plate } from 'features/shared/ui/Page';
import YearSelector from 'features/shared/ui/YearSelector';
import DownloadOlympiadSchedule from './components/DownloadOlympiadSchedule';
import { Space } from '@gr-monorepo/common/ui';
import { educationYears, OlympiadStatus, olympStatusValues, useOlympiadStore } from './useOlympiadStore';
import DownloadGratitudeLetters from './components/OlympiadDetails/DownloadGratitudeLetters/DownloadGratitudeLetters';
import OlympiadsStatisticMiniAppLink from './components/OlympiadsStatisticMiniAppLink';

const Olympiads = () => {
  const userData = useUserData();
  const userType = userData!.userType;
  const {
    currentYear,
    setCurrentYear,
    olympStatus,
    setOlympStatus,
    pendingTotal,
    totalStatistic,
    currentOlympList,
    selectedCurrentYear,
  } = useOlympiadStore();

  const { logPageEvent } = useClickStreamContext();
  const pendingOlympList = currentOlympList.pending || !currentOlympList.loaded;
  const noOlympiadsLabels = useMemo(() => {
    if (!selectedCurrentYear) return 'Нет олимпиад за выбранный год';
    if (olympStatus.id === OlympiadStatus.current) return 'Нет текущих Олимпиад';
    if (olympStatus.id === OlympiadStatus.passed) return 'Нет завершенных олимпиад';
    if (olympStatus.id === OlympiadStatus.future) return 'Нет будущих Олимпиад';
  }, [selectedCurrentYear, olympStatus]);

  const showRating = useMemo(
    () =>
      olympStatus.id !== OlympiadStatus.future &&
      !userData?.azTests.disableRatingMechanics &&
      +currentYear.id.slice(0, 4) > 2022,
    [olympStatus, selectedCurrentYear, userData, currentYear.id],
  );

  return (
    <Container>
      <Header>
        Олимпиады
        <Spacer size={24} style={{ display: 'inline-block' }} />
        <YearSelector
          values={educationYears}
          value={currentYear}
          handleChange={(value) => {
            setCurrentYear(value);
            logPageEvent?.({
              eventType: 'olymp_change_year',
              eventInfo: `Олимпиада: Клик по табу с выбором уч. года года ${value.label}`,
            });
          }}
        ></YearSelector>
      </Header>

      <Text>
        <Paragraph>
          Онлайн-олимпиады Учи.ру помогают школьникам узнавать больше по предметам и развивать <br />
          гибкие навыки. Участие бесплатное. Специальной подготовки не требуется.
        </Paragraph>
        <Paragraph>
          Ряд олимпиад входит в перечень Минпросвещения России. Партнерами выступают АНО <br /> «Национальные
          приоритеты», государственные органы и технологические корпорации.
        </Paragraph>
        <Paragraph>
          Предложите школам присоединиться к интеллектуальным соревнованиям. В зависимости от <br /> результата ученики
          получат сертификат, грамоту или диплом.
        </Paragraph>
      </Text>

      <Plate>
        {pendingTotal ? (
          <LoaderComponent />
        ) : (
          <GeneralStatistics
            totalStatistic={totalStatistic!}
            educationYear={currentYear?.label}
            selectedCurrentYear={selectedCurrentYear}
          />
        )}
      </Plate>
      <Space size={40} axis={'vertical'} />
      {selectedCurrentYear && (
        <>
          <DownloadOlympiadSchedule yearLearn={currentYear?.id || ''} />
          <Space size={24} axis={'vertical'} />
          <OlympiadsStatisticMiniAppLink cabmunId={userData?.id || 0} />
          <Space size={40} axis={'vertical'} />
        </>
      )}
      <Plate>
        {!pendingTotal && selectedCurrentYear ? (
          <>
            <ContentSwitcherWrapper>
              <SwitchTabs
                tabRenderer={BluePillsTab}
                values={olympStatusValues}
                value={olympStatus}
                handleChange={(value) => {
                  setOlympStatus(value);
                  logPageEvent?.({
                    eventType: 'olymp_change_status',
                    eventInfo: `Олимпиада: Клик по табу с выбором состояния олимпиады ${value.id}`,
                  });
                }}
                type={SwitchTabsType.pills}
              />
            </ContentSwitcherWrapper>
            <Space axis="vertical" size={40} />
          </>
        ) : null}
        {olympStatus.id === 'passed' && !(userData!.isRegion || userData!.isLargeCity) ? (
          <DownloadGratitudeLetters />
        ) : null}
        {!pendingTotal && !pendingOlympList ? (
          <div>
            {currentOlympList.olympiads?.length === 0 && <NoOlympiads>{noOlympiadsLabels}</NoOlympiads>}
            {currentOlympList.olympiads?.map((el) => (
              <OlympiadDetails
                key={el.id + el.name + el.dates}
                data={el}
                showRating={showRating}
                status={olympStatus.id}
                isRegionUser={userData!.isRegion || userData!.isLargeCity}
                userType={userType}
                largeCity={userData!.largeCity}
              />
            ))}
          </div>
        ) : !pendingTotal ? (
          <LoaderComponent />
        ) : null}
      </Plate>
    </Container>
  );
};

export default Olympiads;
